import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useSearchParams, useNavigate } from "react-router-dom";
import { UserCircle, Clock, CheckCircle2, XCircle, Hash, Calendar, Timer, Eye, ArrowLeft, MessageSquare, Send, ShoppingBag, Receipt, CreditCard, FileText, Loader2, Repeat, Wallet, TrendingUp, CalendarClock, Ban, Pencil, Camera, Save, X, ListTodo, Ticket, LogOut, Phone, Bell, LayoutGrid, Paperclip, Download, ChevronRight } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { useRealtime } from "../context/RealtimeContext";
import { useAuth } from "../context/AuthContext";
import RightRail from "../components/RightRail";

const eur = (n) => `${(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const TSTATUS = { todo: "À faire", in_progress: "En cours", pending_validation: "Validation en attente", validated: "Validé", refused: "Refusé", open: "Ouvert", answered: "Répondu", converted: "Converti", closed: "Fermé", auto_closed: "Fermé auto" };
const TSTATUS_COLOR = { todo: "#A1A1AA", in_progress: "#3B82F6", pending_validation: "#F59E0B", validated: "#10B981", refused: "#E60000", open: "#10B981", answered: "#3B82F6", converted: "#8B5CF6", closed: "#A1A1AA", auto_closed: "#A1A1AA" };

function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—"; }
function fmtDur(s) { if (!s) return "0 min"; const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60); return h ? `${h}h ${m}min` : `${m} min`; }

const TSTEPS = ["Reçu", "Pris en charge", "En cours", "Résolu"];
function ticketStageIndex(status) {
  if (["converted", "closed", "auto_closed"].includes(status)) return 3;
  if (status === "answered") return 2;
  if (status === "open") return 0;
  return 1;
}
function TicketProgress({ status }) {
  const idx = ticketStageIndex(status);
  return (
    <div className="flex items-center mt-3" data-testid="ticket-progress">
      {TSTEPS.map((label, i) => {
        const done = i < idx, current = i === idx;
        const col = done || current ? "#3B82F6" : "rgba(255,255,255,0.15)";
        return (
          <React.Fragment key={label}>
            <div className="flex flex-col items-center" style={{ width: 64 }}>
              <div className={`w-3.5 h-3.5 rounded-full flex items-center justify-center ${current ? "ring-4 ring-klein/20 animate-pulse" : ""}`} style={{ background: col }}>
                {done && <CheckCircle2 size={10} className="text-zinc-950" />}
              </div>
              <span className={`text-[9px] mt-1 text-center font-mono-label uppercase tracking-wide ${done || current ? "text-klein" : "text-zinc-500"}`}>{label}</span>
            </div>
            {i < TSTEPS.length - 1 && <div className="flex-1 h-0.5 -mt-4 rounded" style={{ background: i < idx ? "#3B82F6" : "rgba(255,255,255,0.12)" }} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function ClientPortalPage() {
  const { events } = useRealtime();
  const { user, setUser, logout } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const asId = params.get("as");
  const asName = params.get("name");
  const q = asId ? `?as=${asId}` : "";
  const [tab, setTab] = useState("home");
  const [editOpen, setEditOpen] = useState(false);
  const [openTask, setOpenTask] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [commentDraft, setCommentDraft] = useState({});
  const [shopCompany, setShopCompany] = useState("");
  const [shopItems, setShopItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [budget, setBudget] = useState(null);
  const [modules, setModules] = useState(null);
  const [buying, setBuying] = useState(null);

  const load = useCallback(async () => {
    const [t, tk, c] = await Promise.all([api.get(`/client/tasks${q}`), api.get(`/client/tickets${q}`), api.get(`/client/companies${q}`)]);
    setTasks(t.data); setTickets(tk.data); setCompanies(c.data);
    setShopCompany((prev) => prev || c.data[0]?.id || "");
  }, [q]);
  useEffect(() => { load(); }, [load, events]);

  // Boutique / abonnements / budget : charge pour l'entreprise sélectionnée
  const loadShop = useCallback(async () => {
    if (!shopCompany) { setShopItems([]); setOrders([]); setSubs([]); setBudget(null); setModules(null); return; }
    const [s, o, su, b, br] = await Promise.all([
      api.get(`/client/shop`, { params: { company_id: shopCompany } }).then((r) => r.data).catch(() => []),
      api.get(`/client/orders`, { params: { company_id: shopCompany } }).then((r) => r.data).catch(() => []),
      api.get(`/client/subscriptions`, { params: { company_id: shopCompany } }).then((r) => r.data).catch(() => []),
      api.get(`/client/budget`, { params: { company_id: shopCompany } }).then((r) => r.data).catch(() => null),
      api.get(`/client/branding`, { params: { company_id: shopCompany } }).then((r) => r.data.client_modules).catch(() => null),
    ]);
    setShopItems(s); setOrders(o); setSubs(su); setBudget(b); setModules(br);
  }, [shopCompany]);
  useEffect(() => { loadShop(); }, [loadShop, events]);

  // Si l'onglet actif est désactivé par l'entreprise, bascule vers un onglet autorisé
  useEffect(() => {
    if (!modules) return;
    const map = { tasks: "tasks", tickets: "tickets", chat: "chat", shop: "orders", subs: "orders", orders: "orders" };
    if (modules[map[tab]] === false) {
      const first = ["tasks", "tickets", "chat", "shop", "subs", "orders"].find((k) => modules[map[k]] !== false);
      if (first) setTab(first);
    }
  }, [modules, tab]);

  // Retour de paiement Stripe : poll du statut
  useEffect(() => {
    if (params.get("checkout") === "cancel") { toast.info("Paiement annulé"); navigate("/portal", { replace: true }); return; }
    const sid = params.get("session_id");
    if (params.get("checkout") === "success" && sid) {
      setTab("orders");
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const { data } = await api.get(`/payments/status/${sid}`);
          if (data.payment_status === "paid") { toast.success("Paiement réussi — merci !"); loadShop(); navigate("/portal", { replace: true }); return; }
          if (data.status === "expired") { toast.error("Session de paiement expirée"); navigate("/portal", { replace: true }); return; }
        } catch {}
        if (attempts < 6) setTimeout(poll, 2000);
        else { toast.info("Paiement en cours de confirmation…"); navigate("/portal", { replace: true }); }
      };
      poll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buy = async (item) => {
    if (asId) { toast.info("Mode aperçu : achat désactivé"); return; }
    setBuying(item.id);
    try {
      const { data } = await api.post(`/client/shop/checkout`, { company_id: shopCompany, item_id: item.id, qty: 1, origin_url: window.location.origin });
      if (data.type === "checkout" && data.url) { window.location.href = data.url; return; }
      if (data.type === "quote") { toast.success(`Devis ${data.number} créé — l'entreprise va le valider`); loadShop(); }
    } catch (e) { toast.error("Commande impossible"); }
    finally { setBuying(null); }
  };

  const paySub = async (sub) => {
    if (asId) { toast.info("Mode aperçu : paiement désactivé"); return; }
    try {
      const { data } = await api.post(`/client/subscriptions/${sub.id}/pay`, { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch { toast.error("Paiement impossible"); }
  };
  const cancelSub = async (sub) => {
    if (asId) { toast.info("Mode aperçu : action désactivée"); return; }
    if (!window.confirm(`Résilier l'abonnement « ${sub.item_name} » ?`)) return;
    await api.post(`/client/subscriptions/${sub.id}/cancel`);
    toast.success("Abonnement résilié");
    loadShop();
  };

  const validate = async (task, decision) => {
    if (asId) { toast.info("Mode aperçu : validation désactivée"); return; }
    await api.post(`/tasks/${task.id}/validate`, { decision });
    toast.success(decision === "validated" ? "Livraison acceptée" : "Livraison refusée");
    load();
  };

  const sendComment = async (task) => {
    if (asId) { toast.info("Mode aperçu : commentaire désactivé"); return; }
    const text = (commentDraft[task.id] || "").trim();
    if (!text) return;
    await api.post(`/tasks/${task.id}/comments`, { text });
    setCommentDraft((d) => ({ ...d, [task.id]: "" }));
    toast.success("Commentaire envoyé");
    load();
  };

  useEffect(() => {
    if (openTask) { const f = tasks.find((t) => t.id === openTask.id); if (f && f !== openTask) setOpenTask(f); }
  }, [tasks]); // eslint-disable-line react-hooks/exhaustive-deps
  const totalTime = tasks.reduce((a, t) => a + (t.time_spent_seconds || 0), 0);
  const pending = tasks.filter((t) => t.status === "pending_validation");
  const spaceCompany = companies[0] || null;
  const scopedCompanies = spaceCompany ? [spaceCompany] : [];
  const NAV = [
    ["home", "Tableau de bord", LayoutGrid, null],
    ["tasks", "Mes tâches", ListTodo, "tasks"],
    ["tickets", "Mes tickets", Ticket, "tickets"],
    ["chat", "Chats", MessageSquare, "chat"],
    ["shop", "Services", ShoppingBag, "orders"],
    ["subs", "Abonnements", Repeat, "orders"],
    ["orders", "Factures", Receipt, "orders"],
  ].filter(([, , , mod]) => !modules || modules[mod] !== false);
  const activeLabel = (NAV.find(([k]) => k === tab) || [null, "Espace client"])[1];
  const [notifOpen, setNotifOpen] = useState(false);

  return (
    <div className="flex flex-col h-[100dvh] overflow-hidden fd-rail-bg">
      {asId && (
        <div data-testid="impersonation-banner" className="bg-klein text-zinc-950 px-4 py-2.5 flex items-center justify-between text-sm font-medium">
          <span className="flex items-center gap-2"><Eye size={16} /> Aperçu en tant que client{asName ? ` : ${decodeURIComponent(asName)}` : ""} (lecture seule)</span>
          <button data-testid="exit-impersonation" onClick={() => navigate("/companies")} className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-zinc-950/20 hover:bg-zinc-950/30"><ArrowLeft size={14} /> Quitter l'aperçu</button>
        </div>
      )}
      {/* Header pleine largeur — relie les deux sidebars */}
      <header data-testid="portal-header" className="h-16 shrink-0 flex items-center gap-4 px-4 lg:px-6 relative z-30">
        <h2 className="font-cabinet font-black text-lg tracking-tight truncate text-white">{activeLabel}</h2>
        <div className="hidden lg:flex items-center gap-4 ml-auto text-sm">
          {spaceCompany?.phone && (
            <a href={`tel:${spaceCompany.phone}`} data-testid="portal-company-phone" className="flex items-center gap-1.5 text-zinc-300 hover:text-klein transition-colors">
              <span className="w-7 h-7 rounded-lg bg-klein/20 text-klein flex items-center justify-center"><Phone size={14} /></span>
              <span className="font-medium">{spaceCompany.phone}</span>
            </a>
          )}
          {spaceCompany?.hours && (
            <div data-testid="portal-company-hours" className="flex items-center gap-1.5 text-zinc-300">
              <span className="w-7 h-7 rounded-lg bg-white/[0.08] flex items-center justify-center"><Clock size={14} /></span>
              <span>{spaceCompany.hours}</span>
            </div>
          )}
        </div>
        <div className={`flex items-center gap-1.5 ${spaceCompany?.phone || spaceCompany?.hours ? "" : "ml-auto"}`}>
          <div className="relative">
            <button data-testid="portal-notif-btn" onClick={() => setNotifOpen((o) => !o)} className="relative w-10 h-10 rounded-full flex items-center justify-center text-zinc-300 hover:text-white hover:bg-white/[0.08] transition-colors">
              <Bell size={19} />
              {pending.length > 0 && <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full bg-amber-400 ring-2 ring-[#0a1730]" />}
            </button>
            {notifOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setNotifOpen(false)} />
                <div data-testid="portal-notif-panel" className="absolute right-0 top-full mt-2 z-40 w-72 bg-card border border-white/10 rounded-2xl shadow-2xl p-2">
                  <p className="px-2 py-1.5 font-mono-label text-[10px] uppercase tracking-widest text-zinc-500">Notifications</p>
                  {pending.length === 0 ? (
                    <p className="px-2 py-4 text-sm text-zinc-400 text-center">Aucune notification pour l'instant.</p>
                  ) : pending.map((t) => (
                    <button key={t.id} data-testid={`notif-item-${t.id}`} onClick={() => { setTab("tasks"); setNotifOpen(false); }} className="w-full flex items-start gap-2.5 px-2.5 py-2 rounded-lg hover:bg-white/[0.06] text-left">
                      <span className="w-7 h-7 rounded-lg bg-amber-400/15 text-amber-400 flex items-center justify-center shrink-0 mt-0.5"><CheckCircle2 size={15} /></span>
                      <span className="min-w-0"><span className="block text-sm font-medium truncate">{t.title}</span><span className="block text-[11px] text-zinc-500">Livrable à valider</span></span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          {!asId && (
            <button data-testid="portal-header-profile" onClick={() => setEditOpen(true)} className="w-9 h-9 rounded-full overflow-hidden bg-zinc-800 flex items-center justify-center ring-2 ring-transparent hover:ring-klein/40 transition-all">
              {user?.avatar_url ? <img src={user.avatar_url} alt="" className="w-full h-full object-cover" /> : <UserCircle size={22} className="text-zinc-400" />}
            </button>
          )}
        </div>
      </header>

      {/* Body : sidebar + carte flottante + outils */}
      <div className="flex-1 flex min-h-0">
      {/* Menu de gauche — Espace client de marque */}
      <aside className="hidden md:flex w-[240px] shrink-0 flex-col pb-3 px-3" data-testid="client-sidebar">
        <div className="flex items-center gap-2.5 px-2 mb-5">
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center overflow-hidden shrink-0 ring-1 ring-black/10" data-testid="portal-company-logo">
            {spaceCompany?.logo_url ? <img src={spaceCompany.logo_url} alt={spaceCompany.name} className="w-full h-full object-cover" /> : <span className="font-cabinet font-black" style={{ color: spaceCompany?.brand_color || "#002FA7" }}>{(spaceCompany?.name || "E").slice(0, 1).toUpperCase()}</span>}
          </div>
          <div className="min-w-0">
            <div className="font-cabinet font-bold text-sm tracking-tight truncate text-white" data-testid="portal-company-name">{spaceCompany?.name || "Espace client"}</div>
            <div className="text-[11px] text-zinc-400">Espace client</div>
          </div>
        </div>
        <nav className="flex-1 space-y-1">
          {NAV.map(([k, l, Icon]) => {
            const active = tab === k;
            return (
            <button key={k} data-testid={`portal-nav-${k}`} onClick={() => setTab(k)}
              className={`group relative w-full flex items-center gap-3 h-11 px-3.5 rounded-full text-sm font-medium transition-colors ${active ? "bg-klein/15 text-klein font-semibold" : "text-zinc-400 hover:bg-foreground/[0.06] hover:text-white"}`}>
              {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-klein" />}
              <Icon size={18} className="shrink-0" /> <span className="flex-1 text-left truncate">{l}</span>
              {k === "tasks" && pending.length > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-amber-400 text-zinc-950 text-[11px] font-bold flex items-center justify-center">{pending.length}</span>}
            </button>
            );
          })}
        </nav>
        <div className="pt-3 border-t border-white/10 space-y-1">
          {!asId && (
            <button data-testid="portal-edit-profile" onClick={() => setEditOpen(true)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-zinc-300 hover:bg-white/[0.08] hover:text-white transition-colors">
              <div className="w-6 h-6 rounded-full bg-zinc-800 overflow-hidden flex items-center justify-center shrink-0">{user?.avatar_url ? <img src={user.avatar_url} alt="" className="w-full h-full object-cover" /> : <UserCircle size={16} />}</div>
              <span className="truncate">{user?.name || "Mon profil"}</span>
            </button>
          )}
          {!asId && <button data-testid="portal-logout" onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-zinc-400 hover:bg-signal/10 hover:text-signal transition-colors"><LogOut size={17} /> Déconnexion</button>}
        </div>
      </aside>

      <main data-testid="portal-main-card" className="flex-1 min-w-0 min-h-0 overflow-y-auto bg-background lg:mb-2 lg:mr-2 lg:rounded-2xl border border-black/5 dark:border-white/[0.06] shadow-[0_8px_30px_rgba(0,0,0,0.18)] relative z-10">
      {/* Barre d'onglets horizontale (mobile uniquement) */}
      <div className="md:hidden flex gap-1 p-2 overflow-x-auto border-b border-white/10 bg-card">
        {NAV.map(([k, l, Icon]) => (
          <button key={k} data-testid={`portal-tab-${k}`} onClick={() => setTab(k)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap ${tab === k ? "bg-klein text-white" : "text-zinc-500"}`}><Icon size={14} /> {l}</button>
        ))}
      </div>
      <div className="p-6 lg:p-10 max-w-5xl mx-auto">
        {editOpen && <ProfileEditModal user={user} setUser={setUser} company={spaceCompany} onClose={() => setEditOpen(false)} />}

        {openTask && <TaskDetailModal task={openTask} onClose={() => setOpenTask(null)} validate={validate} commentDraft={commentDraft} setCommentDraft={setCommentDraft} sendComment={sendComment} readOnly={!!asId} />}

        {tab === "home" ? (
          <DashboardTab tasks={tasks} tickets={tickets} totalTime={totalTime} pending={pending} openTask={setOpenTask} goTab={setTab} company={spaceCompany} user={user} />
        ) : tab === "shop" ? (
          <ShopTab companies={scopedCompanies} shopCompany={shopCompany} setShopCompany={setShopCompany} items={shopItems} buy={buy} buying={buying} readOnly={!!asId} />
        ) : tab === "chat" ? (
          <ChatTab companies={scopedCompanies} defaultCompany={shopCompany} setDefaultCompany={setShopCompany} events={events} readOnly={!!asId} />
        ) : tab === "subs" ? (
          <SubsTab subs={subs} paySub={paySub} cancelSub={cancelSub} readOnly={!!asId} />
        ) : tab === "orders" ? (
          <OrdersTab orders={orders} />
        ) : tab === "tasks" ? (
          <TasksTable tasks={tasks} openTask={setOpenTask} />
        ) : (
          <TicketsTable tickets={tickets} />
        )}
      </div>
      </main>
      <RightRail />
      </div>
    </div>
  );
}


const SALE_LABEL = { quote: ["Demande de devis", "#3B82F6"], instant: ["Achat direct", "#10B981"], subscription: ["Abonnement", "#8B5CF6"] };
const PAY_LABEL = { paid: ["Payé", "#10B981"], initiated: ["En attente", "#F59E0B"], open: ["En attente", "#F59E0B"], expired: ["Expiré", "#EF4444"], complete: ["Payé", "#10B981"] };

function ShopTab({ companies, shopCompany, setShopCompany, items, buy, buying, readOnly }) {
  return (
    <div data-testid="portal-shop">
      {companies.length > 1 && (
        <div className="mb-4">
          <label className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400 block mb-1">Entreprise</label>
          <select data-testid="shop-company-select" value={shopCompany} onChange={(e) => setShopCompany(e.target.value)} className="bg-card border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-klein/20 max-w-xs">
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}
      {items.length === 0 ? (
        <div className="bg-card border border-white/10 rounded-md p-10 text-center text-sm text-zinc-400"><ShoppingBag size={42} strokeWidth={1} className="mx-auto mb-3" />Aucun service disponible à la commande pour l'instant.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="shop-grid">
          {items.map((it) => {
            const ttc = it.unit_price * (1 + (it.vat_rate || 0) / 100);
            const [label, col] = SALE_LABEL[it.sale_mode] || SALE_LABEL.quote;
            const sub = it.sale_mode === "subscription";
            return (
              <div key={it.id} data-testid={`shop-item-${it.id}`} className="bg-card border border-white/10 rounded-2xl p-5 flex flex-col">
                <div className="flex items-center justify-between mb-1">
                  {it.category && <span className="text-[10px] font-mono-label uppercase tracking-widest text-zinc-500">{it.category}</span>}
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold ml-auto" style={{ background: col + "22", color: col }}>{label}</span>
                </div>
                <h3 className="font-cabinet font-bold text-base">{it.name}</h3>
                {it.description && <p className="text-xs text-zinc-400 mt-1 line-clamp-3 flex-1">{it.description}</p>}
                <div className="mt-3 mb-3">
                  <span className="font-cabinet font-black text-2xl text-klein">{eur(ttc)}</span>
                  <span className="text-xs text-zinc-500"> TTC{sub ? (it.interval === "year" ? " / an" : " / mois") : ` / ${it.unit}`}</span>
                </div>
                <button data-testid={`shop-buy-${it.id}`} disabled={buying === it.id || readOnly} onClick={() => buy(it)}
                  className="w-full h-10 rounded-lg bg-klein text-zinc-950 font-semibold text-sm flex items-center justify-center gap-1.5 hover:brightness-110 disabled:opacity-50">
                  {buying === it.id ? <Loader2 size={15} className="animate-spin" /> : it.sale_mode === "instant" ? <CreditCard size={15} /> : it.sale_mode === "subscription" ? <CreditCard size={15} /> : <FileText size={15} />}
                  {it.sale_mode === "instant" ? "Acheter" : it.sale_mode === "subscription" ? "S'abonner" : "Demander un devis"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const THEAD = "text-left text-zinc-400 text-[11px] font-mono-label uppercase tracking-widest border-b border-white/10 bg-white/[0.02]";

function OrdersTab({ orders }) {
  if (orders.length === 0) return <div className="bg-card border border-white/10 rounded-2xl p-10 text-center text-sm text-zinc-400" data-testid="portal-orders"><Receipt size={42} strokeWidth={1} className="mx-auto mb-3 text-zinc-500" />Aucune facture pour l'instant.</div>;
  return (
    <div className="rounded-2xl border border-white/10 bg-card overflow-hidden" data-testid="portal-orders">
      <table className="w-full text-sm">
        <thead><tr className={THEAD}><th className="px-4 py-3 font-medium">Article</th><th className="px-4 py-3 font-medium hidden sm:table-cell">Type</th><th className="px-4 py-3 font-medium hidden md:table-cell">Date</th><th className="px-4 py-3 font-medium">Statut</th><th className="px-4 py-3 font-medium text-right">Montant</th></tr></thead>
        <tbody>
          {orders.map((o) => {
            const [pl, pc] = PAY_LABEL[o.payment_status] || PAY_LABEL.initiated;
            const [sl, sc] = SALE_LABEL[o.sale_mode] || SALE_LABEL.instant;
            return (
              <tr key={o.id} data-testid={`order-${o.id}`} className="border-b border-white/[0.06] last:border-0 hover:bg-white/[0.03] transition-colors">
                <td className="px-4 py-3 font-medium">{o.item_name}{o.qty > 1 ? ` ×${o.qty}` : ""}<div className="md:hidden text-[11px] text-zinc-500 mt-0.5">{new Date(o.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</div></td>
                <td className="px-4 py-3 hidden sm:table-cell"><span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background: sc + "22", color: sc }}>{sl}</span></td>
                <td className="px-4 py-3 text-zinc-400 hidden md:table-cell">{new Date(o.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</td>
                <td className="px-4 py-3"><span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background: pc + "22", color: pc }}>{pl}</span></td>
                <td className="px-4 py-3 text-right tabular-nums font-medium">{eur(o.amount)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const SUB_STATUS = { active: ["Actif", "#10B981"], pending: ["En attente de paiement", "#F59E0B"], canceled: ["Résilié", "#71717a"], past_due: ["Échéance dépassée", "#EF4444"] };
function fmtDay(iso) { return iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—"; }

function SubsTab({ subs, paySub, cancelSub, readOnly }) {
  if (!subs || subs.length === 0) return <div className="bg-card border border-white/10 rounded-2xl p-10 text-center text-sm text-zinc-400" data-testid="portal-subs"><Repeat size={42} strokeWidth={1} className="mx-auto mb-3 text-zinc-500" />Aucun abonnement actif.</div>;
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="rounded-2xl border border-white/10 bg-card overflow-hidden" data-testid="portal-subs">
      <table className="w-full text-sm">
        <thead><tr className={THEAD}><th className="px-4 py-3 font-medium">Abonnement</th><th className="px-4 py-3 font-medium hidden sm:table-cell">Montant</th><th className="px-4 py-3 font-medium hidden lg:table-cell">Prochaine échéance</th><th className="px-4 py-3 font-medium">Statut</th><th className="px-4 py-3 font-medium text-right">Actions</th></tr></thead>
        <tbody>
          {subs.map((s) => {
            const [label, col] = SUB_STATUS[s.status] || SUB_STATUS.active;
            const due = s.next_due_date && s.next_due_date.slice(0, 10) <= today;
            const per = s.interval === "year" ? "an" : "mois";
            return (
              <tr key={s.id} data-testid={`sub-${s.id}`} className="border-b border-white/[0.06] last:border-0 hover:bg-white/[0.03] transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2"><Repeat size={14} className="text-klein shrink-0" /><span className="font-cabinet font-bold truncate">{s.item_name}</span></div>
                  <div className="sm:hidden text-[11px] text-zinc-400 mt-0.5">{eur(s.amount)} / {per}</div>
                </td>
                <td className="px-4 py-3 hidden sm:table-cell tabular-nums text-zinc-300">{eur(s.amount)} <span className="text-zinc-500 text-xs">/ {per}</span></td>
                <td className="px-4 py-3 hidden lg:table-cell text-zinc-300">{s.status === "active" && s.next_due_date ? fmtDay(s.next_due_date) : "—"}</td>
                <td className="px-4 py-3"><span className="text-[11px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap" style={{ background: col + "22", color: col }}>{label}</span></td>
                <td className="px-4 py-3">
                  {s.status !== "canceled" ? (
                    <div className="flex items-center justify-end gap-2">
                      <button data-testid={`sub-pay-${s.id}`} disabled={readOnly} onClick={() => paySub(s)} className={`h-8 px-3 rounded-lg text-xs font-semibold flex items-center gap-1.5 ${due || s.status === "pending" ? "bg-klein text-zinc-950" : "border border-white/10 text-zinc-300 hover:bg-white/[0.06]"} disabled:opacity-50`}><CreditCard size={13} /> {s.status === "pending" ? "Activer" : "Payer"}</button>
                      <button data-testid={`sub-cancel-${s.id}`} disabled={readOnly} onClick={() => cancelSub(s)} title="Résilier" className="h-8 px-2 rounded-lg border border-white/10 text-signal hover:bg-signal/10 flex items-center gap-1 disabled:opacity-50"><Ban size={13} /></button>
                    </div>
                  ) : <span className="block text-right text-xs text-zinc-500">—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function BudgetTab({ budget }) {
  if (!budget) return <div className="bg-card border border-white/10 rounded-md p-10 text-center text-sm text-zinc-400" data-testid="portal-budget"><Wallet size={42} strokeWidth={1} className="mx-auto mb-3" />Aucune donnée de budget.</div>;
  const cats = Object.entries(budget.by_category || {}).sort((a, b) => b[1] - a[1]);
  const maxCat = Math.max(1, ...cats.map(([, v]) => v));
  return (
    <div className="space-y-5" data-testid="portal-budget">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={<Wallet size={16} />} label="Dépensé ce mois" value={eur(budget.month_spent)} />
        <Stat icon={<TrendingUp size={16} />} label="Dépensé cette année" value={eur(budget.year_spent)} />
        <Stat icon={<Repeat size={16} />} label="Récurrent / mois" value={eur(budget.monthly_recurring)} accent />
        <Stat icon={<CalendarClock size={16} />} label="Abonnements actifs" value={budget.active_subscriptions} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-card border border-white/10 rounded-2xl p-5">
          <h3 className="font-cabinet font-bold mb-3 flex items-center gap-2"><CalendarClock size={16} className="text-klein" /> Prochaines échéances</h3>
          {(budget.upcoming || []).length === 0 ? <p className="text-sm text-zinc-500">Aucune échéance à venir.</p> : (
            <div className="space-y-2">
              {budget.upcoming.map((u, i) => (
                <div key={i} data-testid={`budget-upcoming-${i}`} className="flex items-center justify-between text-sm border-b border-white/[0.06] pb-2 last:border-0">
                  <span>{u.name} <span className="text-zinc-500 text-xs">/ {u.interval === "year" ? "an" : "mois"}</span></span>
                  <span className="flex items-center gap-3"><span className="text-zinc-400 text-xs">{fmtDay(u.due_date)}</span><span className="tabular-nums font-medium">{eur(u.amount)}</span></span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-card border border-white/10 rounded-2xl p-5">
          <h3 className="font-cabinet font-bold mb-3 flex items-center gap-2"><Wallet size={16} className="text-klein" /> Répartition par service</h3>
          {cats.length === 0 ? <p className="text-sm text-zinc-500">Aucune dépense enregistrée.</p> : (
            <div className="space-y-2.5">
              {cats.map(([cat, val]) => (
                <div key={cat} data-testid={`budget-cat-${cat}`}>
                  <div className="flex justify-between text-xs mb-1"><span className="text-zinc-300">{cat}</span><span className="tabular-nums text-zinc-400">{eur(val)}</span></div>
                  <div className="h-2 rounded-full bg-white/[0.06]"><div className="h-2 rounded-full bg-klein" style={{ width: `${(val / maxCat) * 100}%` }} /></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChatTab({ companies, defaultCompany, setDefaultCompany, events, readOnly }) {
  const { user } = useAuth();
  const [convId, setConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const cid = defaultCompany || companies[0]?.id || "";
  const endRef = React.useRef(null);

  const openConv = useCallback(async () => {
    if (!cid) return;
    try {
      const { data } = await api.post("/client/conversation", { company_id: cid });
      setConvId(data.id);
    } catch { setConvId(null); }
  }, [cid]);
  useEffect(() => { openConv(); }, [openConv]);

  const loadMsgs = useCallback(async () => {
    if (!convId) return;
    try { const { data } = await api.get(`/conversations/${convId}/messages`); setMessages(data); }
    catch {}
  }, [convId]);
  useEffect(() => { loadMsgs(); }, [loadMsgs, events]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const text = draft.trim();
    if (!text || !convId || readOnly) { if (readOnly) toast.info("Mode aperçu : envoi désactivé"); return; }
    setSending(true);
    try {
      await api.post(`/conversations/${convId}/messages`, { type: "text", text });
      setDraft(""); await loadMsgs();
    } catch { toast.error("Envoi impossible"); }
    finally { setSending(false); }
  };

  const myId = user?.id;

  return (
    <div data-testid="portal-chat" className="bg-card border border-white/10 rounded-2xl flex flex-col" style={{ height: "60vh", minHeight: 380 }}>
      {companies.length > 1 && (
        <div className="p-3 border-b border-white/10">
          <select data-testid="chat-company-select" value={cid} onChange={(e) => setDefaultCompany(e.target.value)} className="bg-background border border-white/10 rounded-lg px-3 py-2 text-sm outline-none max-w-xs">
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}
      <div className="flex-1 overflow-y-auto p-4 space-y-2.5 fd-chat-bg">
        {messages.length === 0 && <div className="h-full flex items-center justify-center text-sm text-zinc-400">Démarrez la discussion avec votre interlocuteur.</div>}
        {messages.map((m) => {
          const mine = m.sender_id === myId;
          return (
            <div key={m.id} data-testid={`chat-msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-klein text-zinc-950 rounded-br-sm" : "bg-background border border-white/10 rounded-bl-sm"}`}>
                {!mine && <div className="text-[10px] font-semibold text-zinc-400 mb-0.5">{m.sender_name}</div>}
                <p className="whitespace-pre-wrap break-words">{m.type === "text" ? m.text : "📎 Pièce jointe"}</p>
                <div className={`text-[9px] mt-0.5 ${mine ? "text-zinc-800/70" : "text-zinc-500"}`}>{new Date(m.created_at).toLocaleString("fr-FR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="p-3 border-t border-white/10 flex items-center gap-2">
        <input data-testid="chat-input" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder="Écrire un message…" className="flex-1 bg-background border border-white/10 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20" />
        <button data-testid="chat-send" onClick={send} disabled={sending || !draft.trim()} className="w-10 h-10 rounded-full bg-klein text-zinc-950 flex items-center justify-center disabled:opacity-40"><Send size={16} /></button>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, accent }) {
  return (
    <div className="bg-card border border-white/10 rounded-2xl p-4">
      <div className="flex items-center gap-1.5 text-zinc-400 mb-1.5">{icon}<span className="font-mono-label text-[10px] uppercase tracking-widest">{label}</span></div>
      <div className={`font-cabinet font-black text-2xl ${accent ? "text-klein" : ""}`}>{value}</div>
    </div>
  );
}

function DashboardTab({ tasks, tickets, totalTime, pending, openTask, goTab, company, user }) {
  const inProgress = tasks.filter((t) => t.status === "in_progress");
  const openTickets = tickets.filter((t) => !["closed", "auto_closed"].includes(t.status));
  const stats = [
    { icon: <ListTodo size={16} />, label: "Tâches en cours", value: inProgress.length, tab: "tasks" },
    { icon: <CheckCircle2 size={16} />, label: "À valider", value: pending.length, accent: pending.length > 0 },
    { icon: <Ticket size={16} />, label: "Tickets ouverts", value: openTickets.length, tab: "tickets" },
    { icon: <Timer size={16} />, label: "Temps passé", value: fmtDur(totalTime), accent: true },
  ];
  return (
    <div className="space-y-6" data-testid="portal-dashboard">
      <div>
        <h3 className="font-cabinet font-black text-2xl tracking-tight">Bonjour {(user?.name || "").split(" ")[0] || ""} 👋</h3>
        <p className="text-sm text-zinc-500">Voici le récapitulatif de vos projets avec {company?.name || "notre équipe"}.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s, i) => (
          <button key={i} onClick={() => s.tab && goTab(s.tab)} data-testid={`dash-stat-${i}`} className={`text-left bg-card border rounded-2xl p-4 transition-colors ${s.tab ? "hover:border-klein/40 cursor-pointer" : "cursor-default"} ${s.accent ? "border-klein/30" : "border-white/10"}`}>
            <div className={`flex items-center gap-1.5 mb-1.5 ${s.accent ? "text-klein" : "text-zinc-400"}`}>{s.icon}<span className="font-mono-label text-[10px] uppercase tracking-widest">{s.label}</span></div>
            <div className={`font-cabinet font-black text-2xl ${s.accent ? "text-klein" : ""}`}>{s.value}</div>
          </button>
        ))}
      </div>

      {/* Validations en attente — uniquement ici */}
      <div className="bg-card border border-white/10 rounded-2xl p-5" data-testid="dash-validations">
        <div className="flex items-center gap-2 mb-3"><span className="w-8 h-8 rounded-xl bg-amber-400/15 text-amber-500 flex items-center justify-center"><CheckCircle2 size={17} /></span><h4 className="font-cabinet font-bold text-base">Validations en attente</h4>{pending.length > 0 && <span className="ml-auto text-xs font-bold bg-amber-400 text-zinc-950 px-2 py-0.5 rounded-full">{pending.length}</span>}</div>
        {pending.length === 0 ? (
          <p className="text-sm text-zinc-400">Aucune livraison en attente de validation pour l'instant. 🎉</p>
        ) : (
          <div className="space-y-2">
            {pending.map((t) => (
              <button key={t.id} data-testid={`dash-pending-${t.id}`} onClick={() => openTask(t)} className="group w-full flex items-center gap-3 bg-background border border-amber-500/25 rounded-xl p-3 hover:border-amber-500/60 transition-colors text-left">
                <span className="w-9 h-9 rounded-lg bg-amber-400/15 text-amber-500 flex items-center justify-center shrink-0"><FileText size={16} /></span>
                <span className="min-w-0 flex-1"><span className="block text-sm font-semibold truncate">{t.title}</span><span className="block text-[11px] text-zinc-500">{t.ref} · Cliquez pour voir la livraison et valider</span></span>
                <ChevronRight size={18} className="shrink-0 text-amber-500 group-hover:translate-x-0.5 transition-transform" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DeliverableFiles({ task }) {
  const delivs = (task.deliverables || []).filter((d) => (d.attachments && d.attachments.length) || (d.links && d.links.length) || d.note);
  if (delivs.length === 0) return <p className="text-sm text-zinc-500">Aucun fichier de livraison joint pour l'instant.</p>;
  const fileHref = (a) => (typeof a === "string" ? fileUrl(a) : (a.url || (a.id ? fileUrl(a.id) : "#")));
  const fileName = (a) => (typeof a === "string" ? "Fichier" : (a.name || a.filename || "Fichier"));
  return (
    <div className="space-y-3">
      {delivs.map((d, i) => (
        <div key={i} className="bg-background border border-white/10 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2"><span className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400">Livraison v{d.version || i + 1}</span>{d.created_at && <span className="text-[10px] text-zinc-500 ml-auto">{new Date(d.created_at).toLocaleDateString("fr-FR")}</span>}</div>
          {d.note && <p className="text-sm text-zinc-300 mb-2 whitespace-pre-wrap">{d.note}</p>}
          <div className="flex flex-wrap gap-2">
            {(d.attachments || []).map((a, j) => (
              <a key={j} href={fileHref(a)} target="_blank" rel="noreferrer" data-testid="deliverable-file" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-klein/10 text-klein text-xs font-medium hover:bg-klein/20"><Paperclip size={13} /> {fileName(a)} <Download size={12} /></a>
            ))}
            {(d.links || []).map((l, j) => (
              <a key={`l${j}`} href={typeof l === "string" ? l : l.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.06] text-zinc-200 text-xs font-medium hover:bg-white/10"><FileText size={13} /> {typeof l === "string" ? "Lien" : (l.label || "Lien")}</a>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function TaskDetailModal({ task, onClose, validate, commentDraft, setCommentDraft, sendComment, readOnly }) {
  const t = task;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="task-detail-modal">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-card border border-white/10 rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-card/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-start gap-3 z-10">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono-label text-[10px] text-zinc-400">{t.ref}</span>
              <span className="font-mono-label text-[10px] font-bold text-klein bg-klein/10 px-1.5 py-0.5 rounded">v{t.version || 1}</span>
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border" style={{ color: TSTATUS_COLOR[t.status], borderColor: TSTATUS_COLOR[t.status] + "55", background: TSTATUS_COLOR[t.status] + "18" }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: TSTATUS_COLOR[t.status] }} />{TSTATUS[t.status]}</span>
            </div>
            <h3 className="font-cabinet font-black text-xl tracking-tight">{t.title}</h3>
          </div>
          <button data-testid="task-detail-close" onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-zinc-400 hover:bg-white/[0.06]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          {t.description && <div><p className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Description</p><p className="text-sm text-zinc-300 whitespace-pre-wrap">{t.description}</p></div>}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
            {[["Demande", t.created_at], ["Début", t.started_at], ["Deadline", t.deadline], ["Fin", t.resolved_at]].map(([l, v]) => (
              <div key={l}><div className="font-mono-label uppercase tracking-widest text-zinc-400 flex items-center gap-1"><Calendar size={10} />{l}</div><div className="text-zinc-300 mt-0.5">{fmtDate(v)}</div></div>
            ))}
          </div>

          <div>
            <p className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400 mb-2 flex items-center gap-1.5"><Paperclip size={11} /> Livraison à consulter</p>
            <DeliverableFiles task={t} />
          </div>

          {t.status === "pending_validation" && !readOnly && (
            <div className="flex gap-2 pt-1" data-testid="task-detail-validate-actions">
              <button data-testid={`task-validate-${t.id}`} onClick={() => { validate(t, "validated"); onClose(); }} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-emerald-500 text-zinc-950 text-sm font-semibold hover:bg-emerald-600"><CheckCircle2 size={16} /> Accepter la livraison</button>
              <button data-testid={`task-refuse-${t.id}`} onClick={() => { validate(t, "refused"); onClose(); }} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-signal text-white text-sm font-semibold hover:bg-red-700"><XCircle size={16} /> Demander une révision</button>
            </div>
          )}

          <div className="pt-4 border-t border-white/10 space-y-2" data-testid={`portal-comments-${t.id}`}>
            <p className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5"><MessageSquare size={11} /> Échanges</p>
            {(t.comments || []).length === 0 && <p className="text-xs text-zinc-500">Aucun message pour le moment.</p>}
            {(t.comments || []).map((c) => (
              <div key={c.id} className="flex gap-2 text-sm">
                <div className="w-6 h-6 rounded-full bg-zinc-800 shrink-0 flex items-center justify-center text-[10px] font-bold text-zinc-300 overflow-hidden">{c.author_avatar ? <img src={c.author_avatar} alt="" className="w-full h-full object-cover" /> : (c.author_name || "?").slice(0, 1).toUpperCase()}</div>
                <div className="min-w-0 flex-1 bg-background border border-white/10 rounded-lg px-2.5 py-1.5">
                  <div className="flex items-center gap-2"><span className="text-xs font-medium text-zinc-200">{c.author_name}</span><span className="text-[10px] text-zinc-500 ml-auto">{new Date(c.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span></div>
                  <p className="text-sm text-zinc-300 whitespace-pre-wrap">{c.text}</p>
                </div>
              </div>
            ))}
            {!readOnly && (
              <div className="flex gap-2 pt-1">
                <input data-testid={`portal-comment-input-${t.id}`} value={commentDraft[t.id] || ""} onChange={(e) => setCommentDraft((d) => ({ ...d, [t.id]: e.target.value }))} onKeyDown={(e) => { if (e.key === "Enter") sendComment(t); }} placeholder="Répondre / poser une question…" className="flex-1 bg-background border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
                <button data-testid={`portal-comment-send-${t.id}`} onClick={() => sendComment(t)} className="px-3 rounded-lg bg-klein text-zinc-950"><Send size={14} /></button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileEditModal({ user, setUser, company, onClose }) {
  const nameParts = (user?.name || "").trim().split(" ");
  const [form, setForm] = useState({
    firstName: nameParts[0] || "",
    lastName: nameParts.slice(1).join(" ") || "",
    phone: user?.phone || "",
    company_name: user?.company_name || "",
    title: user?.title || "",
    avatar_url: user?.avatar_url || "",
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadAvatar = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      set("avatar_url", fileUrl(data.id));
      toast.success("Photo prête — pensez à enregistrer");
    } catch { toast.error("Échec de l'envoi de la photo"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    const fullName = `${form.firstName} ${form.lastName}`.trim();
    if (!fullName) { toast.error("Le prénom et le nom sont requis"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch("/profile", { name: fullName, phone: form.phone, avatar_url: form.avatar_url, company_name: form.company_name, title: form.title });
      setUser(data);
      toast.success("Profil mis à jour");
      onClose();
    } catch { toast.error("Échec de l'enregistrement"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="portal-profile-modal">
      <div className="w-full max-w-md bg-card border border-white/10 rounded-2xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-cabinet font-bold text-xl tracking-tight">Mon profil</h2>
          <button data-testid="portal-profile-close" onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-zinc-400 hover:bg-white/[0.06]"><X size={18} /></button>
        </div>

        <div className="flex items-center gap-4 mb-5">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-zinc-900 overflow-hidden flex items-center justify-center text-white ring-2 ring-white/10">
              {form.avatar_url ? <img src={form.avatar_url} alt="" className="w-full h-full object-cover" /> : <UserCircle size={40} />}
            </div>
            <label className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-klein text-zinc-950 flex items-center justify-center cursor-pointer hover:brightness-110 shadow-lg" data-testid="portal-avatar-upload">
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />}
              <input type="file" accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="portal-avatar-input" />
            </label>
          </div>
          <div className="flex-1">
            <p className="text-xs text-zinc-500 mb-2">Votre photo ou le logo de votre entreprise. JPG ou PNG.</p>
            {company?.logo_url && (
              <button type="button" data-testid="portal-use-company-logo" onClick={() => set("avatar_url", company.logo_url)} className="text-xs font-medium text-klein hover:underline flex items-center gap-1"><ShoppingBag size={12} className="rotate-0" /> Utiliser le logo de {company.name}</button>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400 block mb-1">Prénom</label>
              <input data-testid="portal-profile-firstname" value={form.firstName} onChange={(e) => set("firstName", e.target.value)} className="w-full bg-background border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
            </div>
            <div>
              <label className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400 block mb-1">Nom</label>
              <input data-testid="portal-profile-lastname" value={form.lastName} onChange={(e) => set("lastName", e.target.value)} className="w-full bg-background border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
            </div>
          </div>
          <div>
            <label className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400 block mb-1">Téléphone</label>
            <input data-testid="portal-profile-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="06 12 34 56 78" className="w-full bg-background border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
          </div>
          <div>
            <label className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400 block mb-1">Email</label>
            <input value={user?.email || ""} disabled className="w-full bg-background/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-zinc-500 cursor-not-allowed" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400 block mb-1">Fonction</label>
              <input data-testid="portal-profile-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Gérant, Marketing…" className="w-full bg-background border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
            </div>
            <div>
              <label className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-400 block mb-1">Entreprise représentée</label>
              <input data-testid="portal-profile-company" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Votre société" className="w-full bg-background border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
            </div>
          </div>
        </div>

        <button data-testid="portal-profile-save" onClick={save} disabled={saving} className="mt-5 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-klein text-zinc-950 font-semibold text-sm hover:brightness-110 disabled:opacity-60">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Enregistrer
        </button>
      </div>
    </div>
  );
}


function StatusBadge({ status, small }) {
  const col = TSTATUS_COLOR[status] || "#A1A1AA";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full font-medium border whitespace-nowrap ${small ? "px-1.5 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]"}`} style={{ color: col, borderColor: col + "55", background: col + "18" }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: col }} />{TSTATUS[status] || status}
    </span>
  );
}

function CompactProgress({ status }) {
  const idx = ticketStageIndex(status);
  return (
    <div className="flex items-center" data-testid="ticket-progress">
      {TSTEPS.map((label, i) => (
        <React.Fragment key={label}>
          <span className={`w-2 h-2 rounded-full shrink-0 ${i <= idx ? "bg-klein" : "bg-white/15"}`} title={label} />
          {i < TSTEPS.length - 1 && <span className={`w-5 h-0.5 rounded ${i < idx ? "bg-klein" : "bg-white/[0.12]"}`} />}
        </React.Fragment>
      ))}
      <span className="ml-2 text-[11px] text-zinc-400 whitespace-nowrap">{TSTEPS[idx]}</span>
    </div>
  );
}

function TasksTable({ tasks, openTask }) {
  if (tasks.length === 0) return <div className="bg-card border border-white/10 rounded-2xl p-10 text-center text-sm text-zinc-400" data-testid="portal-tasks"><ListTodo size={42} strokeWidth={1} className="mx-auto mb-3 text-zinc-500" />Aucune tâche pour le moment.</div>;
  return (
    <div className="rounded-2xl border border-white/10 bg-card overflow-hidden" data-testid="portal-tasks">
      <table className="w-full text-sm">
        <thead>
          <tr className={THEAD}>
            <th className="px-4 py-3 font-medium">Tâche</th>
            <th className="px-4 py-3 font-medium hidden sm:table-cell">Statut</th>
            <th className="px-4 py-3 font-medium hidden lg:table-cell">Échéance</th>
            <th className="px-4 py-3 font-medium hidden md:table-cell text-right">Temps passé</th>
            <th className="px-2 py-3 w-8"></th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((t) => (
            <tr key={t.id} data-testid={`portal-task-${t.id}`} onClick={() => openTask(t)}
              className="group border-b border-white/[0.06] last:border-0 hover:bg-white/[0.03] cursor-pointer transition-colors"
              style={{ boxShadow: `inset 3px 0 0 ${TSTATUS_COLOR[t.status]}` }}>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="font-cabinet font-bold truncate">{t.title}</span>
                  {t.status === "pending_validation" && <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-500">À valider</span>}
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-[11px] text-zinc-500">
                  <span className="font-mono-label">{t.ref}</span>
                  <span className="font-mono-label font-bold text-klein">v{t.version || 1}</span>
                  <span className="sm:hidden"><StatusBadge status={t.status} small /></span>
                </div>
              </td>
              <td className="px-4 py-3 hidden sm:table-cell"><StatusBadge status={t.status} /></td>
              <td className="px-4 py-3 hidden lg:table-cell text-zinc-300"><span className="inline-flex items-center gap-1.5"><Calendar size={12} className="text-zinc-500" />{fmtDate(t.deadline)}</span></td>
              <td className="px-4 py-3 hidden md:table-cell text-right"><span className="inline-flex items-center gap-1 font-mono-label text-klein"><Timer size={13} />{fmtDur(t.time_spent_seconds)}</span></td>
              <td className="px-2 py-3 text-right"><ChevronRight size={16} className="text-zinc-500 group-hover:text-klein group-hover:translate-x-0.5 transition-transform inline" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TicketsTable({ tickets }) {
  if (tickets.length === 0) return <div className="bg-card border border-white/10 rounded-2xl p-10 text-center text-sm text-zinc-400" data-testid="portal-tickets"><Ticket size={42} strokeWidth={1} className="mx-auto mb-3 text-zinc-500" />Aucun ticket pour le moment.</div>;
  return (
    <div className="rounded-2xl border border-white/10 bg-card overflow-hidden" data-testid="portal-tickets">
      <table className="w-full text-sm">
        <thead>
          <tr className={THEAD}>
            <th className="px-4 py-3 font-medium">Demande</th>
            <th className="px-4 py-3 font-medium hidden lg:table-cell">Progression</th>
            <th className="px-4 py-3 font-medium hidden sm:table-cell">Statut</th>
            <th className="px-4 py-3 font-medium hidden md:table-cell">Entreprise</th>
            <th className="px-4 py-3 font-medium text-right">Créé le</th>
          </tr>
        </thead>
        <tbody>
          {tickets.map((t) => {
            const open = !["converted", "closed", "auto_closed"].includes(t.status);
            return (
              <tr key={t.id} data-testid={`portal-ticket-${t.id}`} className="border-b border-white/[0.06] last:border-0 hover:bg-white/[0.03] transition-colors" style={{ boxShadow: `inset 3px 0 0 ${TSTATUS_COLOR[t.status]}` }}>
                <td className="px-4 py-3">
                  <div className="font-medium truncate">{t.subject}</div>
                  <div className="flex items-center gap-2 mt-0.5 text-[11px] text-zinc-500">
                    <span className="font-mono-label">{t.ref}</span>
                    {open && t.position ? <span className="inline-flex items-center gap-0.5 text-klein font-medium" data-testid={`queue-position-${t.id}`}><Hash size={10} />position {t.position}</span> : null}
                    <span className="sm:hidden"><StatusBadge status={t.status} small /></span>
                  </div>
                </td>
                <td className="px-4 py-3 hidden lg:table-cell"><CompactProgress status={t.status} /></td>
                <td className="px-4 py-3 hidden sm:table-cell"><StatusBadge status={t.status} /></td>
                <td className="px-4 py-3 hidden md:table-cell text-zinc-400">{t.company_name}</td>
                <td className="px-4 py-3 text-right text-zinc-400 whitespace-nowrap">{fmtDate(t.created_at)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
